import { useContext } from 'react'

import { UPLOAD_LIMIT_BYTE, UPLOAD_LIMIT_GIGABYTE, UPLOAD_LIMIT_POINTS_MILLION } from '@editor/config/constants'
import { GlobalModalContext } from '@editor/contexts/GlobalModal'
import { UserContext } from '@editor/contexts/Users'
import { InspectionArea } from '@editor/interfaces/inspection'
import { Project } from '@editor/interfaces/project'
import { uploadFile } from '@editor/services/AWS'
import { getSignedUrlForUploadFile, updateInspectionAreaFileName } from '@editor/services/InspectionArea'
import { getPointCount, returnContentType } from '@editor/services/Util'
import { checkSize } from '@editor/services/Validation'
import { RootState } from '@editor/store/app'
import dayjs from 'dayjs'
import mixpanel from 'mixpanel-browser'
import { useSelector } from 'react-redux'
import { Cache } from 'three'

/**
 * File definition for uploading a PCD file.
 */
export interface UploadPCDFile {
  file: File
  name: string
  content_type: string
  pointCount: number | null
}

export function useUploadPcd() {
  // Contexts
  const { getAccessToken } = useContext(UserContext)
  const { showErrorModal } = useContext(GlobalModalContext)

  // Store
  const userProfile = useSelector((root: RootState) => root.user.userProfile)

  /**
   * Process the selected file and return the file information.
   *
   * @param e - The event containing the selected file
   */
  const processSelectedFile = async (e: React.ChangeEvent<HTMLInputElement>): Promise<UploadPCDFile | false> => {
    e.preventDefault()
    if (!e || !e.target || !e.target.files || !e.target.files.length) return false
    const file = e.target.files[0]
    const extension: string | undefined = file.name.split('.').pop()

    // Check total storage limit
    if (!checkSize(file.size + (userProfile?.stats?.total_size || 0), UPLOAD_LIMIT_BYTE)) {
      showErrorModal(`アップロードファイルの合計が${UPLOAD_LIMIT_GIGABYTE}GBを超えています。`)
      return false
    }

    const filename = dayjs().format('YYYYMMDDHHmmss_') + file.name
    const content_type = returnContentType(filename)

    if (!content_type) {
      showErrorModal('ファイル形式に誤りがあります。')
      return false
    }

    // Parse point count from file header
    const pointCount = await getPointCount(file, extension || '')

    // Convert limit from millions to actual count
    const limitPoints = UPLOAD_LIMIT_POINTS_MILLION * 1_000_000

    // Determine if file passes validation
    const accepted = pointCount === null || pointCount <= limitPoints

    // Track the upload attempt in Mixpanel
    mixpanel.track('PCD Upload Attempted', {
      'Point Count': pointCount,
      'File Extension': extension,
      'File Size (bytes)': file.size,
      Accepted: accepted,
      'Parsing Failed': pointCount === null,
    })

    // Check point count limit
    if (pointCount !== null && pointCount > limitPoints) {
      // Convert point counts to millions for display (rounded to 1 decimal)
      const limitMillion = UPLOAD_LIMIT_POINTS_MILLION
      const fileMillion = (pointCount / 1_000_000).toFixed(1)

      showErrorModal(
        `アップロード可能なポイント数の上限（${limitMillion}百万ポイント）を超えています。このファイルには${fileMillion}百万ポイントが含まれています。`,
      )
      return false
    }

    return {
      file,
      name: file.name,
      content_type,
      pointCount,
    }
  }

  /**
   * Upload a PCD file.
   * No need to translate any error messages here, they will not be shown to the user.
   *
   * @param project - The project to which the inspection area will be added
   * @param inspectionArea - The inspection area to be edited. If this is provided, the modal will be in edit mode
   * @param file - The file to upload
   * @param onProgress - A function to call with the upload progress
   * @returns The updated inspection area
   * @throws If any error occurs during the upload process
   */
  const uploadPcd = async (
    project: Project,
    inspectionArea: InspectionArea,
    file: UploadPCDFile,
    onProgress: (value: number) => void,
  ): Promise<InspectionArea> => {
    const token = await getAccessToken()
    if (!token) {
      throw new Error('failed to get access token')
    }

    const presignedUrl = await getSignedUrlForUploadFile(token, file.name, file.content_type, showErrorModal)
    if (!presignedUrl) {
      throw new Error('failed to get presigned url')
    }

    //* ファイルアップロード
    const uploadResult = await uploadFile(presignedUrl, file.content_type, file.file, onProgress, showErrorModal)
    if (!uploadResult) {
      throw new Error('failed to upload file')
    }

    if (inspectionArea?.project_id && inspectionArea.down_sampled_file?.name) {
      Cache.remove(inspectionArea.inspection_area_id)
    }

    //* Add file name to InspectionArea
    const updateResult = await updateInspectionAreaFileName(
      token,
      project.project_id,
      inspectionArea.inspection_area_id,
      file.name,
      showErrorModal,
    )
    if (!updateResult) {
      throw new Error('failed to update inspection area file name')
    }

    return updateResult
  }

  return { processSelectedFile, uploadPcd }
}
